import { Link, useParams } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { api, useApi } from "../api";
import { PageShell } from "../components/Layout";
import { ChapterTag, FaqAccordion, Marquee, SpotlightCard } from "../components/Motion";
import Seo, { breadcrumbSchema, faqSchema } from "../components/Seo";
import { LinkPills, Loading, PageHero, SectionBlock } from "../components/Sections";

export default function CityDetail() {
  const { city } = useParams();
  const { data, loading, error } = useApi(() => api.city(city), [city]);

  if (loading) {
    return (
      <PageShell>
        <Loading />
      </PageShell>
    );
  }
  if (error || !data) {
    return (
      <PageShell>
        <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4" data-testid="city-not-found">
          <p className="font-display font-black uppercase text-4xl text-white">Location not found</p>
          <Link to="/locations" className="font-mono text-xs uppercase tracking-widest text-amber border border-amber px-5 py-3">
            View all UK locations
          </Link>
        </div>
      </PageShell>
    );
  }

  const origin = window.location.origin;
  const c = data.city;
  const marqueeItems = data.services.flatMap((s) => [`${s.name} ${c.name}`, s.name]);

  return (
    <PageShell>
      <Seo title={data.title} description={data.description} path={`/locations/${city}`} schema={faqSchema(data.faqs)} keywords={data.keywords} />
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema(data.breadcrumb, origin))}</script>

      <PageHero eyebrow={data.eyebrow} lines={data.h1_lines} intro={data.intro} breadcrumb={data.breadcrumb} image={data.image} bullets={data.intro_bullets} />
      <Marquee items={marqueeItems.slice(0, 24)} />

      {data.sections.map((sec, i) => (
        <SectionBlock key={i} index={i} heading={sec.heading} paragraphs={sec.paragraphs} bullets={sec.bullets}>
          {sec.links && (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="city-services-grid">
              {sec.links.map((s) => (
                <Link key={s.slug} to={`/locations/${city}/${s.slug}`} data-testid={`city-service-${s.slug}`} className="block h-full">
                  <SpotlightCard className="h-full p-5 hover:border-amber/60 transition-colors duration-300">
                    <div className="flex items-center gap-2 text-amber">
                      <ShieldCheck className="w-4 h-4" />
                      <span className="font-display font-bold uppercase tracking-wide text-base text-white leading-tight">
                        {s.name} {c.name}
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-gray-400 leading-relaxed">{s.short}</p>
                  </SpotlightCard>
                </Link>
              ))}
            </div>
          )}
        </SectionBlock>
      ))}

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-14" data-testid="city-nearby-section">
        <ChapterTag num="GRID" title={`NEARBY — ${c.region.toUpperCase()}`} />
        <p className="max-w-3xl text-sm sm:text-base text-gray-400 leading-relaxed">
          Xtreme Protection operates across the whole of {c.region}. Explore security guard services in nearby towns and
          cities:
        </p>
        <LinkPills
          items={data.nearby}
          prefix="/locations"
          labelFn={(n) => `Security Guards ${n.name}`}
          testIdPrefix="nearby-city"
        />
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-14" data-testid="city-faq-section">
        <ChapterTag num="FAQ" title={`${c.name.toUpperCase()} — QUESTIONS`} />
        <FaqAccordion faqs={data.faqs} testIdPrefix="city-faq" />
      </section>
    </PageShell>
  );
}
